
import { supabase } from '@/integrations/supabase/client';
import { uploadFileToStorage } from '../fileUploadUtils';
import { deleteTrackFile } from './deleteTrack';

export async function updateTrack(
  trackId: string,
  updates: {
    spotifyUrl?: string;
    trackName?: string;
    description?: string;
    file?: File | null;
    artistId?: string;
  }
): Promise<boolean> {
  try {
    const updateData: Record<string, any> = {};
    
    if (updates.spotifyUrl) updateData.spotify_url = updates.spotifyUrl;
    if (updates.trackName) updateData.track_title = updates.trackName;
    if (updates.description !== undefined) updateData.description = updates.description;
    
    // If a new file is provided, upload it
    if (updates.file && updates.artistId) {
      console.log("Uploading new file for track update:", updates.file.name);
      
      // Get the track details to get the spotify URL for the upload and the old file path
      const { data: trackData, error: fetchError } = await supabase
        .from('tracks')
        .select('spotify_url, file_url')
        .eq('id', trackId)
        .single();
      
      if (fetchError) {
        console.error("Error fetching track for file upload:", fetchError);
        throw new Error("Failed to get track details for file upload");
      }
      
      // Delete the original file before uploading the new one
      if (trackData.file_url) {
        console.log("Deleting original file:", trackData.file_url);
        
        // Use the dedicated deleteTrackFile utility to remove the old file
        const { success, error } = await deleteTrackFile(trackData.file_url);
        
        if (!success) {
          console.error("Error deleting original file:", error);
          // Log the error but continue with the update
        } else {
          console.log("Original file successfully deleted from storage");
        }
      }
      
      // Upload the new file
      const uploadResult = await uploadFileToStorage(
        trackData.spotify_url,
        updates.file,
        updates.artistId
      );
      
      if (!uploadResult || uploadResult.error) {
        console.error("Error uploading new file:", uploadResult?.error);
        throw new Error("Failed to upload new file");
      }
      
      // Add file details to the update data
      updateData.file_url = uploadResult.publicUrl;
      updateData.file_name = updates.file.name;
      updateData.file_type = updates.file.name.split('.').pop() || 'unknown';
      updateData.file_size = updates.file.size;
    }
    
    console.log(`Updating track ${trackId} with:`, updateData);
    
    const { error } = await supabase
      .from('tracks')
      .update(updateData)
      .eq('id', trackId);
    
    if (error) {
      console.error("Error updating track:", error);
      throw new Error(error.message || "Failed to update track");
    }
    
    return true;
  } catch (error) {
    console.error("Track update error:", error);
    return false;
  }
}
